import { isJsonObject } from "../../../api/http/jsonDecoding";
import { formatCalendarDate } from "../../../shared/formatting/dateTimes";
import type { NestedDescription, StatementValue } from "./aboxViewModel";

/**
 * How a project's values read as one value (Daniel's viewer, the bar since 4 Nov): a date from its year, month and day
 * rows ("26 Aug 1455"), an amount from its value and unit ("8 marks of silver"); and what heads each text, its date and
 * archive reference. All of it data in the view profile (`dmw-abox-view/1`); DMW names no project's terms.
 */
export interface TextFactsShape {
  readonly classes: readonly string[];
  readonly date: ReadonlySet<string>;
  readonly archive: ReadonlySet<string>;
}

export interface DateReadingShape {
  readonly classes: readonly string[];
  readonly year: string;
  readonly month: string | null;
  readonly day: string | null;
  readonly calendar: string | null;
  readonly expression: string | null;
}

export interface AmountReadingShape {
  readonly classes: readonly string[];
  readonly value: string;
  readonly unit: string;
}

const iris = (value: unknown): string[] => (Array.isArray(value) ? value.filter((iri): iri is string => typeof iri === "string") : []);
const iri = (value: unknown): string | null => (typeof value === "string" ? value : null);

export function textFactsOf(value: unknown): TextFactsShape | null {
  if (!isJsonObject(value)) return null;
  return { classes: iris(value["classes"]), date: new Set(iris(value["date"])), archive: new Set(iris(value["archive"])) };
}

export function dateReadingOf(value: unknown): DateReadingShape | null {
  const year = isJsonObject(value) ? iri(value["year"]) : null;
  if (!isJsonObject(value) || year === null) return null;
  return { classes: iris(value["classes"]), year, month: iri(value["month"]), day: iri(value["day"]), calendar: iri(value["calendar"]), expression: iri(value["expression"]) };
}

export function amountReadingOf(value: unknown): AmountReadingShape | null {
  const amount = isJsonObject(value) ? iri(value["value"]) : null;
  const unit = isJsonObject(value) ? iri(value["unit"]) : null;
  if (!isJsonObject(value) || amount === null || unit === null) return null;
  return { classes: iris(value["classes"]), value: amount, unit };
}

type Row = NestedDescription["rows"][number];
type Value = Row["values"][number];

/**
 * A date as it reads: its words in the reader's language, the calendar it names, the source's own words, and the rows
 * the reading doesn't use, which show after it, so nothing the data says is dropped (Sol R6's rule).
 */
export interface ReadDate {
  readonly text: string;
  readonly year: number;
  readonly calendar: StatementValue | null;
  readonly original: string | null;
  readonly rest: readonly Row[];
}
const textOf = (value: Value | undefined): string | null => (value?.kind === "text" ? value.text : null);
const firstOf = (description: NestedDescription, property: string | null): Value | undefined =>
  property === null ? undefined : description.rows.find((row) => row.property === property)?.values[0];
/** "1455", "--08", "---26" (XSD gYear, gMonth, gDay): the number, or `null`. */
const numberOf = (text: string | null): number | null => {
  const digits = text?.replace(/^-+/, "").match(/^\d+/)?.[0];
  return digits === undefined ? null : Number(digits);
};

/** A described date as one date, or `null` when it isn't one by the profile's reading; it then shows as written. */
export function readDate(description: NestedDescription, shape: DateReadingShape | null, isOf: (classes: readonly string[]) => boolean, language: string): ReadDate | null {
  if (shape === null || !isOf(shape.classes)) return null;
  const year = numberOf(textOf(firstOf(description, shape.year)));
  if (year === null) return null;
  const month = numberOf(textOf(firstOf(description, shape.month)));
  const day = month === null ? null : numberOf(textOf(firstOf(description, shape.day)));
  const text = formatCalendarDate(year, month, day, language);
  const found = firstOf(description, shape.calendar);
  const calendar = found === undefined || found.kind === "nested" ? null : found;
  const original = textOf(firstOf(description, shape.expression));
  // Only a row the reading shows counts as used, and only when it holds one value: a day without a month, a calendar
  // described further or a second value stay as rows (Sol R9; Sol R6's rule).
  const shows = (property: string | null, shown: boolean) => (property !== null && shown ? [property] : []);
  const used = new Set([...shows(shape.year, true), ...shows(shape.month, month !== null), ...shows(shape.day, day !== null), ...shows(shape.calendar, calendar !== null), ...shows(shape.expression, original !== null)]);
  return { text, year, calendar, original, rest: description.rows.filter((row) => !used.has(row.property) || row.values.length > 1) };
}

/** A described amount as one amount, its value and unit, with the rows the reading doesn't use; `null` when it isn't one. */
export function readAmount(description: NestedDescription, shape: AmountReadingShape | null, isOf: (classes: readonly string[]) => boolean): { readonly value: string; readonly unit: Value; readonly rest: readonly Row[] } | null {
  if (shape === null || !isOf(shape.classes)) return null;
  const value = textOf(firstOf(description, shape.value));
  const unit = firstOf(description, shape.unit);
  if (value === null || unit === undefined) return null;
  return { value, unit, rest: description.rows.filter((row) => row.property !== shape.value && row.property !== shape.unit) };
}
